import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

interface GSTNumberInputProps {
  // Controlled pattern (used by JourneyPage)
  value?: { gst_number?: string | null };
  onChange?: (val: { gst_number: string }) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  // Legacy pattern
  initialData?: { gst_number?: string };
  onDataChange?: (data: { gst_number: string }) => void;
}

// GST format: 2 digits state code + 10 char PAN + 1 digit entity number + 1 char Z + 1 check digit
// e.g. 27ABCDE1234F1Z5
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

function validateGST(value: string): string {
  const trimmed = (value || '').trim();
  // GST is optional — only validate if something is entered
  if (!trimmed) return '';
  if (trimmed.length !== 15) return 'GST number must be exactly 15 characters';
  if (!GST_REGEX.test(trimmed)) return 'Invalid GST format (e.g. 27ABCDE1234F1Z5)';
  return '';
}

const GSTNumberInput: React.FC<GSTNumberInputProps> = ({
  value,
  onChange,
  disabled,
  registerSubmit,
  initialData,
  onDataChange,
}) => {
  const gstNumber = (value?.gst_number ?? initialData?.gst_number ?? '') || '';

  const [touched, setTouched] = useState(false);

  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(async () => {
      setTouched(true);
      const gst = (valueRef.current?.gst_number ?? '') || '';
      return validateGST(gst) === '';
    });
  }, [registerSubmit]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
    onChange?.({ gst_number: val });
    onDataChange?.({ gst_number: val });
  }, [onChange, onDataChange]);

  const error = touched ? validateGST(gstNumber) : '';

  return (
    <div className="space-y-1.5">
      <Label htmlFor="gst_number" className="text-gray-500 font-medium">
        GST Number
      </Label>
      <Input
        id="gst_number"
        type="text"
        maxLength={15}
        value={gstNumber}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        placeholder="15-digit GSTIN (optional)"
        disabled={disabled}
        className={`border-gray-200 focus:border-intellend-green focus:ring-intellend-green rounded-xl py-6 uppercase ${error ? 'border-red-400 focus:border-red-500' : ''}`}
      />
      {error && (
        <p className="text-xs text-red-500 mt-0.5">{error}</p>
      )}
    </div>
  );
};

export default GSTNumberInput;
